import * as R from 'ramda';
import { clone, exclude, extend, find, map, reduce, some } from 'lodash';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs/observable/of';
import Card from '@app/card';
import settings from '@app/app.settings';

@Injectable()
export class GridService {
  activeSection: string;
  cardsBySection = {};
  centralCards: Card[] = [];
  initialPanelCards: Card[] = [];
  isErrorMessageShown = false;
  options: {maxCols: number, maxRows: number};
  panelCards: Card[] = [];
  currentLayout: string = settings.defaultGridLayout;
  currentScreenWidth: string;

  cardNeighbors(card) {
    const
      isTall = card.rows === 2,
      isWide = card.cols === 2;

    return reduce(
      this.centralCards,

      (neightbors, gridCard) => {
        // Left neightbors
        if (gridCard.x + gridCard.cols === card.x) {
          if (gridCard.y === card.y - 1) { neightbors.upLeft = gridCard; }

          if (gridCard.y === card.y || (gridCard.y === card.y - 1 && gridCard.rows === 2)) {
            neightbors.leftOne = gridCard;
          }

          if (gridCard.y === card.y + 1 && card.rows === 2) { neightbors.leftTwo = gridCard; }
          if (gridCard.y === card.y + card.rows) { neightbors.bottomLeft = gridCard; }
        }

        // Right neighbors
        if (gridCard.x === card.x + card.cols) {
          if (gridCard.y === card.y - 1) { neightbors.upRight = gridCard; }

          if (gridCard.y === card.y || (gridCard.y === card.y - 1 && gridCard.rows === 2)) {
            neightbors.rightOne = gridCard;
          }

          if (gridCard.y === card.y + 1) { neightbors.rightTwo = gridCard; }
          if (gridCard.y === card.y + card.rows) { neightbors.bottomRight = gridCard; }
        }

        // Upper neighbors
        if (gridCard.y + gridCard.rows === card.y) {
          if (gridCard.x === card.x) { neightbors.upOne = gridCard; }

          if (gridCard.x === card.x + 1 || (gridCard.x === card.x && gridCard.cols === 2)) {
            neightbors.upTwo = gridCard;
          }
        }

        // Bottom neighbors
        if (gridCard.y === card.y + card.rows) {
          if (gridCard.x === card.x) { neightbors.bottomOne = gridCard; }

          if (gridCard.x === card.x + 1 || (gridCard.x === card.x && gridCard.cols === 2)) {
            neightbors.bottomTwo = gridCard;
          }
        }

        return neightbors;
      },

      {},
    );
  }

  cardsOfActiveSection() {
    return this.cardsBySection[this.activeSection] || [];
  }

  // If card can not be resized without being moved somewhere in the grid then compute coords to move card to
  coordsToMoveTo(card, newLayout) {
    // On click top right resize button
    if (newLayout.cols === 2 && newLayout.rows === 1 && card.x > 0 && this.isCellEmpty(card.x - 1, card.y)) {
      return {x: card.x - 1, y: card.y};
    }

    // On click left bottom resize button
    if (newLayout.cols === 1 && newLayout.rows === 2 && card.y > 0 && this.isCellEmpty(card.x, card.y - 1)) {
      return {x: card.x, y: card.y - 1};
    }

    // On click right bottom resize button when the card is in the top row
    if (newLayout.cols === 2 && newLayout.rows === 2 && card.y === 0 && card.x > 0) {
      if (
        (
          // If the card occupies one cell
          card.rows === 1 && card.cols === 1 &&
          this.isCellEmpty(card.x - 1, card.y) &&
          this.isCellEmpty(card.x - 1, card.y + 1) &&
          this.isCellEmpty(card.x, card.y + 1)
        ) ||

        (
          // If the card occupies two vertical cell
          card.rows === 2 && card.cols === 1 &&
          this.isCellEmpty(card.x - 1, card.y) &&
          this.isCellEmpty(card.x - 1, card.y + 1)
        )
      ) { return {x: card.x - 1, y: card.y}; }
    }

    // On click right bottom resize button
    if (newLayout.cols === 2 && newLayout.rows === 2 && card.y > 0 && card.x > 0) {

      // If the card occupies one cell
      if (
        card.rows === 1 && card.cols === 1 &&
        this.isCellEmpty(card.x - 1, card.y - 1) &&
        this.isCellEmpty(card.x, card.y - 1) &&
        this.isCellEmpty(card.x - 1, card.y)
      ) {
        return {x: card.x - 1, y: card.y - 1};
      }

      // If the card occupies two horizontal cell
      if (
        card.cols === 2 && card.rows === 1 &&
        this.isCellEmpty(card.x, card.y - 1) &&
        this.isCellEmpty(card.x + 1, card.y - 1)
      ) {
        return {x: card.x, y: card.y - 1};
      }

      // If the card occupies two vertical cell
      if (
        card.cols === 1 && card.rows === 2 &&
        this.isCellEmpty(card.x - 1, card.y) &&
        this.isCellEmpty(card.x - 1, card.y + 1)
      ) {
        return {x: card.x - 1, y: card.y};
      }
    }

    return false;
  }

  constructor(private http: HttpClient) { }

  dropPanelCard(item, gridsterItem, event) {
    const
      deletedItemIndex = this.panelCards.indexOf(item),
      target = document.elementsFromPoint(event.clientX, event.clientY);

    if (!target) { return; }

    const
      targetElement = find(target, element => {
        return element.getAttribute('class') === 'empty-grid-layout-cell';
      });

    if (!targetElement) { return; }

    const
      x = parseInt(targetElement.getAttribute('data-x'), 10),
      y = parseInt(targetElement.getAttribute('data-y'), 10),

      droppedCard = {
        ...item,
        rows: (this.currentLayout === 'deck' && (item.text || '').length > settings.maxTextSizeForOneRow) ? 2 : 1,
        x,
        y,
      };

    this.centralCards.push(droppedCard);
    this.panelCards.splice(deletedItemIndex, 1);
    this.removeCardFromPanel(deletedItemIndex);
    return true;
  }

  filterAndSetInitialPanelCards(cards) {
    this.initialPanelCards = this.filterPanelCards(cards);
  }

  filterAndSetPanelCards(cards) {
    this.panelCards = this.filterPanelCards(cards);
  }

  filterPanelCards(cards) {
    return R.filter(card => !R.has('x', card) && !R.has('y', card), cards);
  }

  isCardResizable(card, newLayout) {
    const
      nextX = card.x + newLayout.cols - 1,
      nextY = card.y + newLayout.rows - 1;

    // Resizing down to 1x1 is always available
    if (
      newLayout.cols === 1 && newLayout.rows === 1 ||
      newLayout.cols === 1 && newLayout.rows === card.rows ||
      newLayout.rows === 1 && newLayout.cols === card.cols
    ) { return true; }

    // Don't resize over the grid
    if (nextX >= this.options.maxCols || nextY >= this.options.maxRows) { return false; }

    // debugger;

    // In case of 2x2 resize check for neighbor cards
    if (newLayout.cols === 2 && newLayout.rows === 2) {
      return this.isCellEmpty(nextX, nextY) &&
        (this.isCellEmpty(nextX - 1, nextY) || card.rows === 2) &&
        (this.isCellEmpty(nextX, nextY - 1) || card.cols === 2);
    }

    return this.isCellEmpty(nextX, nextY);
  }

  isCellEmpty(x, y) {
    return !some(
      this.centralCards,
      card => (card.x === x || card.x + (card.cols - 1) === x) && (card.y === y || card.y + (card.rows - 1) === y),
    );
  }

  moveCardWithNeighborTo(params: {card: Card, direction: 'bottom' | 'left' | 'right' | 'up'}) {
    // Return true if params.card is empty, simulate the case of moved card
    if (!params.card) {
      return true;
    }

    const
      cardIndex = this.centralCards.findIndex(gridCard => gridCard.id === params.card.id),
      neighbors = this.cardNeighbors(params.card),
      neighborOne = neighbors[`${params.direction}One`],
      neighborTwo = neighbors[`${params.direction}Two`];

    // Edge of the grid — no space to move a card
    if (
      params.direction === 'up' && (params.card.y === 0) ||
      params.direction === 'left' && (params.card.x === 0) ||
      params.direction === 'bottom' && (params.card.y + params.card.rows >= this.options.maxRows) ||
      params.direction === 'right' && (params.card.x + params.card.cols >= this.options.maxCols)
    ) {
      return false;
    }

    // Check if there are neighbors in the required direction
    if (neighborOne || neighborTwo) {
      // If yes then apply moveCardWithNeighborsTo to neightbors and when done check if there is space again
      return this.moveCardWithNeighborTo({card: neighborOne, direction: params.direction}) &&
        this.moveCardWithNeighborTo({card: neighborTwo, direction: params.direction});
    } else {
      // If not then just move card into required direction
      if (params.direction === 'bottom') { this.setCardNewOptions(cardIndex, {y: this.centralCards[cardIndex].y + 1}); }
      if (params.direction === 'left') { this.setCardNewOptions(cardIndex, {x: this.centralCards[cardIndex].x - 1}); }
      if (params.direction === 'right') { this.setCardNewOptions(cardIndex, {x: this.centralCards[cardIndex].x + 1}); }
      if (params.direction === 'up') { this.setCardNewOptions(cardIndex, {y: this.centralCards[cardIndex].y - 1}); }
      return true;
    }
  }

  removeCardFromGrid(id) {
    this.centralCards.splice(this.centralCards.findIndex(card => card.id === id), 1);
    this.panelCards.push(Object.assign({}, find(this.initialPanelCards, {id})));
  }

  // Move all cards below one removed from right panel upper
  removeCardFromPanel(deletedItemIndex) {
    let
      newArray = this.panelCards.slice(0, deletedItemIndex);

    for (let i = deletedItemIndex; i < this.panelCards.length; i++) {
      const
        item = this.panelCards[i];

      let
        { x, y } = item;

      if (x === 1) {
        x = 0;
      } else if (y > 0) {
        y = y - 1;
        x = 1;
      }

      newArray = newArray.concat({...item, x, y});
    }

    this.panelCards = newArray;
  }

  setActiveSection(id) {
    this.activeSection = id;
    this.centralCards = clone(this.cardsBySection[id] || []);
  }

  setCardLayout(card, newLayout) {
    const
      cardNeighbors = this.cardNeighbors(card),
      indexChangedCard = this.centralCards.indexOf(card),
      isCardAtLeftEdge = card.x === 0,
      isCardAtRightEdge = card.x + card.cols >= this.options.maxCols;

    // Don't resize card if it is already has requested dimension
    if (card.cols === newLayout.cols && card.rows === newLayout.rows) {
      return false;
    }

    if (!this.isCardResizable(card, newLayout)) {
      const
        movableCoords = this.coordsToMoveTo(card, newLayout);

      if (movableCoords) {
        this.setCardNewOptions(indexChangedCard, {
          ...movableCoords,
          ...newLayout,
        });

        return;
      }

      // Recursively move cards if success and there is space apply layout change again
      if (
        // In case of horizontal expand — move cards to left or right
        (
          newLayout.cols <= card.cols ||

          newLayout.cols > card.cols && newLayout.rows === 1 && (
            !isCardAtRightEdge && this.moveCardWithNeighborTo({card: cardNeighbors.rightOne, direction: 'right'}) ||
            !isCardAtLeftEdge && this.moveCardWithNeighborTo({card: cardNeighbors.leftOne, direction: 'left'})
          ) ||

          newLayout.cols > card.cols && newLayout.rows === 2 && (
            !isCardAtRightEdge && (
              this.moveCardWithNeighborTo({card: cardNeighbors.rightOne, direction: 'right'}) &&
              this.moveCardWithNeighborTo({card: cardNeighbors.rightTwo, direction: 'right'})
            ) ||

            !isCardAtLeftEdge && (
              this.moveCardWithNeighborTo({card: cardNeighbors.leftOne, direction: 'left'}) &&
              this.moveCardWithNeighborTo({card: cardNeighbors.leftTwo, direction: 'left'})
            )
          )
        ) &&

        // In case of vertical expand — move cards to top or bottom
        (
          newLayout.rows <= card.rows ||

          newLayout.rows > card.rows && newLayout.cols === 1 && (
            this.moveCardWithNeighborTo({card: cardNeighbors.bottomOne, direction: 'bottom'}) ||
            this.moveCardWithNeighborTo({card: cardNeighbors.upOne, direction: 'up'})
          ) ||

          newLayout.rows > card.rows && newLayout.cols === 2 && (
            (
              this.moveCardWithNeighborTo({card: cardNeighbors.bottomOne, direction: 'bottom'}) &&
              this.moveCardWithNeighborTo({card: cardNeighbors.bottomTwo, direction: 'bottom'})
            ) ||

            (
              this.moveCardWithNeighborTo({card: cardNeighbors.upOne, direction: 'up'}) &&
              this.moveCardWithNeighborTo({card: cardNeighbors.upTwo, direction: 'up'})
            )
          )
        )
      ) {
        this.setCardLayout(card, newLayout);
        return;
      }

      this.isErrorMessageShown = true;
      setTimeout(() => { this.isErrorMessageShown = false; }, 3000);
      return;
    }

    this.setCardNewOptions(indexChangedCard, {
      ...newLayout,
    });
  }

  setCardNewOptions(cardIndex, newOptions) {
    this.centralCards[cardIndex] = {
      ...this.centralCards[cardIndex],
      ...newOptions,
    };
  }

  setCentralCards(cards) {
    this.centralCards = cards;
  }

  setOptions(options) {
    this.options = options;
  }

  setPanelCards(cards) {
    this.panelCards = cards;
  }
}



// WEBPACK FOOTER //
// /Users/nide/Code/3dissue/3d-issue-hubs-ui/src/app/edit/grid.service.ts